//Libraries
import { Card, Flex, Table, TableContainer, Tbody, Td, Text, Tfoot, Th, Thead, Tooltip, Tr } from '@chakra-ui/react';
import { QuestionOutlineIcon } from '@chakra-ui/icons';
// Types
import { CardTableProps, CellMapper } from './CardTable.type';
// Utils
import { renderComponent } from './CardTable.helper';

/**
 * CardTable component represents a table styled as a card.
 *
 * @component
 * @param {CardTableProps} props - CardTable props
 * @param {Table<NumCells>} props.table - The table.
 * @param {VariantsKeys} props.variant - The variant of the card.
 * @param {string} props.title - Optional, indicating the title of the card.
 * @returns {ReactElement} The rendered CardTable component.
 */
export const CardTable = <NumCells extends number>({
  table: { rows, headers, footers },
  variant,
  title,
}: CardTableProps<NumCells>) => (
  <Card variant={variant}>
    <TableContainer w='auto'>
      <Text fontSize='s' color='text.base' fontWeight='medium'>
        {title}
      </Text>
      <Table variant={variant}>
        <Thead>
          <Tr>
            {headers.map(({ id, label, align, tooltip }) => (
              <Th key={`${id}_component_head_card_table_`} p={1}>
                <Flex justifyContent={align} align='center' gap={1}>
                  <Text fontSize='xs' variant='faded' fontWeight='medium' lineHeight={2} noOfLines={2}>
                    {label}
                  </Text>
                  {!!tooltip && (
                    <Tooltip
                      hasArrow
                      placement='top'
                      openDelay={100}
                      closeDelay={200}
                      gutter={12}
                      bg='white'
                      label={
                        <Text fontSize='s' p={1}>
                          {tooltip}
                        </Text>
                      }
                    >
                      <QuestionOutlineIcon
                        transition='all 0.2s ease-in-out'
                        color='grey.100'
                        boxSize={2.5}
                        _hover={{
                          color: 'base',
                        }}
                      />
                    </Tooltip>
                  )}
                </Flex>
              </Th>
            ))}
          </Tr>
        </Thead>
        <Tbody>
          {!!rows &&
            rows.map((row) => (
              <Tr key={JSON.stringify(row)}>
                {row.cells.map(({ component, props, align = 'start' }) => {
                  return (
                    <Td key={`${component}_component_cell_card_table_${JSON.stringify(props)}`} p={3} isNumeric>
                      {CellMapper[component] ? (
                        <Flex justifyContent={align}>{renderComponent(component, props)}</Flex>
                      ) : null}
                    </Td>
                  );
                })}
              </Tr>
            ))}
        </Tbody>
        {!!footers && (
          <Tfoot>
            <Tr>
              {footers.map(({ id, label, align }) => (
                <Td key={`${id}_component_footer_card_table_`} p={1}>
                  <Flex justifyContent={align}>
                    <Text fontSize='xs' variant='faded' fontWeight='medium' lineHeight={2} noOfLines={2}>
                      {label}
                    </Text>
                  </Flex>
                </Td>
              ))}
            </Tr>
          </Tfoot>
        )}
      </Table>
    </TableContainer>
  </Card>
);
